import { formatDate } from '@/_utils/date';
import { RefObject } from 'react';
import { ChartColor, ColorMap, createChartColorFromHex, defaultChartColors } from './chartConfig';

export type CategoryBackground = {
  start: string;
  end: string;
  hover: {
    dark: string;
    light: string;
  };
};

type BaseChartData = {
  labels: string[];
  colors: string[];
  id: string;
};

export interface NonMatrixedChartData extends BaseChartData {
  values: number[];
  legend: 'square' | 'circle' | 'value' | 'complete';
}

export interface MatrixedChartData extends BaseChartData {
  values: MatrixedChartValues[];
  legend: 'square' | 'circle' | 'tile';
  axes: {
    x: string;
    y: string;
  };
}

export type MatrixedChartValues = {
  label: string;
  data: number[];
};

export type ChartData = NonMatrixedChartData | MatrixedChartData;

export const buildChartColors = (
  numberOfValues: number,
  chartColorOverrides: ChartData['colors'] = [],
  colorMap: ColorMap
): ChartColor[] => {
  const chartColors: ChartColor[] = [];
  const filteredDefaultChartColors = defaultChartColors.filter(
    (color) => !chartColorOverrides.includes(color)
  );

  let skippedColorCount = 0;
  for (let i = 0; i < numberOfValues + skippedColorCount; i++) {
    // go through all chart color overrides (default chart override + chart color overrides)
    // if you hit the end of those overrides, iterate throgh all keys in color dictionary dictionary
    const colorString =
      i < chartColorOverrides.length
        ? chartColorOverrides[i]
        : filteredDefaultChartColors[
            (i - chartColorOverrides.length) % filteredDefaultChartColors.length
          ];

    // if the color string is a hex string, create a chart color from it
    if (colorString[0] === '#' || colorString[0] === 'r') {
      chartColors.push(createChartColorFromHex(colorString));
    } else {
      const colorMapEntry = colorMap[colorString];
      if (colorMapEntry) {
        // if a valid color map entry exists, pass along that chart color
        chartColors.push(colorMapEntry);
      } else {
        // if its not a valid chart color, and its not a hex string, skip this index
        // increment the number of skipped colors so we still create an array of colors with the length of numberOfValues
        skippedColorCount++;
      }
    }
  }

  return chartColors;
};

export const generateColorArrayFromConfigs = (
  numValues: number,
  clientDefaults: string[] | undefined = [],
  chartDefaults: string[] | undefined = []
): string[] => chartDefaults.concat(clientDefaults, defaultChartColors).slice(0, numValues);

export const legendMouseOver = (
  chartRef: RefObject<any>,
  index: number,
  matrixed: boolean = false
) => {
  const chart = chartRef.current;
  const tooltip = chart.tooltip;
  const indexes = matrixed
    ? {
        datasetIndex: index,
        index: chart.data.labels.length - 1 // index of last label - ie most recent
      }
    : {
        datasetIndex: 0,
        index: index
      };

  chart.setActiveElements([{ ...indexes }]);
  tooltip.setActiveElements([{ ...indexes }]);
  chart.update();
};

export const legendMouseLeave = (chartRef: RefObject<any> | RefObject<any>[]) => {
  const turnOffTooltip = (ref: any) => {
    const chart: any = ref.current;
    const tooltip = chart.tooltip;
    chart.setActiveElements([]);
    tooltip.setActiveElements([]);
    chart.update();
  };

  if (Array.isArray(chartRef)) {
    for (const ref of chartRef) {
      turnOffTooltip(ref);
    }
  } else {
    turnOffTooltip(chartRef);
  }
};

export const generatePrettyDates = (dates: string[]): string[] => {
  return dates.map((date) => formatDate(date, 'MMM yyyy'));
};

export const roundToNearestFive = (num: number) => {
  return Math.round(num / 5) * 5;
};

// TODO remove this eventually
export const dummyMatrixData = {
  labels: ['Jan', 'Feb', 'Mar'],
  values: [
    {
      label: 'data1',
      data: [150, 120, 180, 160, 200, 230]
    },
    {
      data: [80, 100, 130, 110, 140, 170],
      label: 'data2'
    },
    {
      data: [200, 220, 250, 240, 280, 310],
      label: 'data3'
    },
    {
      data: [100, 320, 150, 340, 80, 350],
      label: 'data4'
    }
  ],
  axes: {
    x: 'Months',
    y: 'Successful Ingest'
  }
};

export const dummyNonMatrixData = {
  labels: ['data1', 'data2', 'data3', 'data4'],
  values: [150, 120, 180, 160]
};

export function generateRandomNumbers(count: number, rangeMax: number) {
  const randomNumbers = [];
  for (let i = 0; i < count; i++) {
    randomNumbers.push(Math.floor(Math.random() * rangeMax)); // Adjust the range as needed
  }
  return randomNumbers;
}

export function abbreviateNumber(num: number) {
  if (num < 1000) {
    return num.toLocaleString();
  } else if (num < 1000000) {
    return '~ ' + (num / 1000).toFixed(1) + 'K';
  } else {
    return '~ ' + (num / 1000000).toFixed(1) + 'M';
  }
}
